// Terrain lives on squares, in the rules, so the search sees it.

import { fileOf, movePath, neighborSquares, rankOf, squareIndex } from './squares';
import type { Board, Rules, Terrain, TerrainKind } from './types';

export const TERRAIN_NAMES: Record<TerrainKind, string> = {
  deadly: 'deadly square',
  slow: 'slow square',
  wall: 'wall',
  removed: 'removed square',
  linked: 'linked square',
  clone: 'clone square',
};

/** What the ground does, for the inspector. */
export const TERRAIN_BLURBS: Record<TerrainKind, string> = {
  deadly: 'A piece that lands here or crosses it is removed.',
  slow: 'A piece standing here moves one square at most.',
  wall: 'Nothing enters and nothing passes through.',
  removed: 'The square is gone from the board. Nothing stands here.',
  linked: 'A piece that lands here comes out at the other end.',
  clone: 'A piece that lands here gets a copy on a random empty square beside it.',
};

export const terrainAt = (rules: Rules, square: number): Terrain | null =>
  rules.terrain[square] ?? null;

/** Nothing may enter or pass through. */
export const isSolid = (rules: Rules, square: number): boolean => {
  const kind = terrainAt(rules, square)?.kind;
  return kind === 'removed' || kind === 'wall';
};

export const isSlow = (rules: Rules, square: number): boolean =>
  terrainAt(rules, square)?.kind === 'slow';

export const isDeadly = (rules: Rules, square: number): boolean =>
  terrainAt(rules, square)?.kind === 'deadly';

/**
 * The squares a move crosses: every square on its path but the two ends. A
 * knight move crosses the two squares of its long leg, the corner included.
 * Until 2026-10-15 a knight crossed none and jumped over a deadly square.
 */
export const crossedSquares = (from: number, to: number): number[] => {
  const path = movePath(from, to);
  return path.slice(1).flatMap((end, index) => {
    const between = squaresBetween(path[index] ?? from, end);
    return end === to ? between : [...between, end];
  });
};

/** The squares strictly between two ends on one rank, file, or diagonal. None otherwise. */
const squaresBetween = (from: number, to: number): number[] => {
  const df = fileOf(to) - fileOf(from);
  const dr = rankOf(to) - rankOf(from);
  const steps = Math.max(Math.abs(df), Math.abs(dr));
  const straight = df === 0 || dr === 0 || Math.abs(df) === Math.abs(dr);
  if (!straight || steps < 2) return [];
  const sf = Math.sign(df);
  const sr = Math.sign(dr);
  return Array.from({ length: steps - 1 }, (_, index) =>
    squareIndex(fileOf(from) + sf * (index + 1), rankOf(from) + sr * (index + 1)),
  );
};

/** The first deadly square the move crosses, or null. */
export const deadlyCrossing = (rules: Rules, from: number, to: number): number | null =>
  crossedSquares(from, to).find((square) => isDeadly(rules, square)) ?? null;

export const linkedExit = (rules: Rules, square: number): number | null => {
  const terrain = terrainAt(rules, square);
  return terrain?.kind === 'linked' && terrain.link !== undefined ? terrain.link : null;
};

export const isClone = (rules: Rules, square: number): boolean =>
  terrainAt(rules, square)?.kind === 'clone';

/**
 * The square the copy takes: an empty neighbor with no terrain. Null when
 * there is none. The seed of the clone square and the id of the piece pick
 * it, so the search and the game get the same square with no random source.
 */
export const cloneSpot = (
  board: Board,
  rules: Rules,
  square: number,
  pieceId: number,
): number | null => {
  const open = neighborSquares(square).filter(
    (neighbor) => board[neighbor] === null && terrainAt(rules, neighbor) === null,
  );
  if (open.length === 0) return null;
  const seed = terrainAt(rules, square)?.seed ?? 0;
  return open[(seed + pieceId) % open.length] ?? null;
};

export const withTerrain = (rules: Rules, squares: readonly number[], terrain: Terrain): Rules => {
  const next = { ...rules.terrain };
  for (const square of squares) next[square] = terrain;
  return { ...rules, terrain: next };
};

/** Counts every timed terrain down one ply and drops what ran out. */
export const tickTerrain = (rules: Rules): { rules: Rules; expired: number[] } => {
  const next: Record<number, Terrain> = {};
  const expired: number[] = [];
  for (const [key, terrain] of Object.entries(rules.terrain)) {
    const square = Number(key);
    if (terrain.plies < 0) {
      next[square] = terrain;
    } else if (terrain.plies > 1) {
      next[square] = { ...terrain, plies: terrain.plies - 1 };
    } else {
      expired.push(square);
    }
  }
  return { rules: { ...rules, terrain: next }, expired };
};

/** Pieces standing on solid terrain fall off the board. */
export const squaresSwallowed = (board: Board, rules: Rules): number[] => {
  const result: number[] = [];
  board.forEach((piece, square) => {
    if (piece && isSolid(rules, square)) result.push(square);
  });
  return result;
};
